import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crowdex/ui/components/dropdown-menu';
import { cn } from '@crowdex/ui/lib/utils';
import { Archive, Check, FolderOpen, SlidersHorizontal, X } from 'lucide-react';
import type { ReactNode } from 'react';
import {
  GROUP_BY_OPTIONS,
  SHOW_OPTIONS,
  type SidebarGroupBy,
  type SidebarShow,
  type SidebarView,
} from '@/lib/desktop-shell/sidebar-model';

type ViewMenuProps = {
  view: SidebarView;
  onChange: (next: Partial<SidebarView>) => void;
  /** How many conversations are archived, when known. */
  archivedCount: number | null;
  /** Archived conversations are found with ⌘K. */
  onShowArchived: () => void;
  /** Adds a repository to work in (a folder, a clone), where the shell can. */
  onAddRepository?: (() => void) | undefined;
};

/** The sidebar's View button and menu (D70): group by, show, the archived ones. */
export function SidebarViewMenu({
  view,
  onChange,
  archivedCount,
  onShowArchived,
  onAddRepository,
  trigger,
}: ViewMenuProps & { trigger?: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {trigger ?? (
          <button
            aria-label="View"
            className="flex size-7 shrink-0 items-center justify-center rounded-control text-subtle-foreground transition-colors hover:bg-control hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring data-[state=open]:bg-control data-[state=open]:text-foreground"
            title="View"
            type="button"
          >
            <SlidersHorizontal className="size-3.5" />
          </button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-80" side="right" sideOffset={10}>
        <DropdownMenuLabel>Group by</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          onValueChange={(value) => onChange({ groupBy: value as SidebarGroupBy })}
          value={view.groupBy}
        >
          {GROUP_BY_OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
              {/* The hint at the row's own size: one size per menu row. */}
              {option.hint !== undefined && (
                <span className="ml-auto truncate pl-3 text-muted-foreground">{option.hint}</span>
              )}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuLabel>Show</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          onValueChange={(value) => onChange({ show: value as SidebarShow })}
          value={view.show}
        >
          {SHOW_OPTIONS.map((option) => (
            <DropdownMenuRadioItem key={option.value} value={option.value}>
              {option.label}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={onShowArchived}>
          <Archive />
          Archived conversations
          {archivedCount !== null && (
            <span className="ml-auto text-muted-foreground tabular-nums">{archivedCount}</span>
          )}
        </DropdownMenuItem>
        {onAddRepository !== undefined && (
          <DropdownMenuItem onSelect={onAddRepository}>
            <FolderOpen />
            Add repository…
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** The phone's View sheet (2c): the same choices, as grouped lists. */
export function SidebarViewSheetContent({
  view,
  onChange,
  archivedCount,
  onShowArchived,
  onAddRepository,
}: ViewMenuProps) {
  return (
    <div className="space-y-4">
      <ViewGroup title="Group by">
        {GROUP_BY_OPTIONS.map((option) => (
          <ViewChoice
            checked={view.groupBy === option.value}
            hint={option.hint}
            key={option.value}
            label={option.label}
            onSelect={() => onChange({ groupBy: option.value })}
          />
        ))}
      </ViewGroup>
      <ViewGroup title="Show">
        {SHOW_OPTIONS.map((option) => (
          <ViewChoice
            checked={view.show === option.value}
            key={option.value}
            label={option.label}
            onSelect={() => onChange({ show: option.value })}
          />
        ))}
      </ViewGroup>
      <div className="overflow-hidden rounded-2xl border bg-card">
        <button
          className="flex min-h-12 w-full items-center gap-3 px-4 text-left active:bg-accent/60"
          onClick={onShowArchived}
          type="button"
        >
          <Archive className="size-5 text-muted-foreground" />
          <span className="flex-1">Archived conversations</span>
          {archivedCount !== null && (
            <span className="text-muted-foreground tabular-nums">{archivedCount}</span>
          )}
        </button>
        {onAddRepository !== undefined && (
          <button
            className="flex min-h-12 w-full items-center gap-3 border-hairline border-t px-4 text-left active:bg-accent/60"
            onClick={onAddRepository}
            type="button"
          >
            <FolderOpen className="size-5 text-muted-foreground" />
            <span className="flex-1">Add repository…</span>
          </button>
        )}
      </div>
    </div>
  );
}

function ViewGroup({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3 className="type-meta mb-1.5 px-1 text-muted-foreground">{title}</h3>
      <div className="divide-y divide-hairline overflow-hidden rounded-2xl border bg-card">
        {children}
      </div>
    </section>
  );
}

function ViewChoice({
  label,
  hint,
  checked,
  onSelect,
}: {
  label: string;
  hint?: string | undefined;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      aria-pressed={checked}
      className="flex min-h-12 w-full items-center gap-3 px-4 text-left active:bg-accent/60"
      onClick={onSelect}
      type="button"
    >
      <Check className={cn('size-4 text-foreground', !checked && 'invisible')} />
      <span className="flex-1">{label}</span>
      {hint !== undefined && <span className="type-meta text-muted-foreground">{hint}</span>}
    </button>
  );
}

/** The active filter under the Conversations heading (D71), removed by its ×. */
export function SidebarFilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <span className="type-caption inline-flex h-6 items-center gap-1 rounded-control bg-control pr-1 pl-2.5 text-foreground">
      {label}
      <button
        aria-label={`Remove the ${label} filter`}
        className="flex size-4 items-center justify-center rounded-full text-muted-foreground hover:text-foreground"
        onClick={onClear}
        type="button"
      >
        <X className="size-3" />
      </button>
    </span>
  );
}
